import React, { useState, useEffect } from 'react';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/utils/api';

const dayName = (d) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long' }) : '';
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export const HolidaySettings = () => {
  const [holidays, setHolidays] = useState([]);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');

  const load = () => api.get('/holidays').then(r => setHolidays(r.data || [])).catch(() => toast.error('Failed to load holidays'));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim() || !date) return toast.error('Enter holiday name and date');
    try {
      await api.post('/holidays', { name: name.trim(), date });
      setName(''); setDate(''); load();
      toast.success('Holiday added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/holidays/${id}`); load(); toast.success('Holiday removed'); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="holiday-settings-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 bg-rose-50 rounded-lg"><CalendarDays className="h-5 w-5 text-rose-600" /></div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Holidays</h2>
          <p className="text-sm text-slate-500">All outlets. Roster employees: Week Off. Others: Paid holiday (Present). Working on a holiday gives +1 day.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-slate-600">Holiday Name</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Diwali" data-testid="holiday-name-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Date</label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="holiday-date-input" />
        </div>
        <div className="min-w-[110px]">
          <label className="text-xs font-medium text-slate-600">Day</label>
          <div className="h-9 flex items-center text-sm text-slate-700" data-testid="holiday-day-display">{dayName(date) || '—'}</div>
        </div>
        <Button onClick={add} data-testid="holiday-add-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <table className="w-full text-sm" data-testid="holiday-table">
        <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Name</th><th>Date</th><th>Day</th><th></th></tr></thead>
        <tbody>
          {holidays.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">No holidays added</td></tr>}
          {holidays.map(h => (
            <tr key={h.id} className="border-b last:border-0" data-testid={`holiday-row-${h.date}`}>
              <td className="py-2 font-medium text-slate-800">{h.name}</td>
              <td>{fmtDate(h.date)}</td>
              <td>{dayName(h.date)}</td>
              <td className="text-right">
                <Button variant="ghost" size="sm" onClick={() => remove(h.id)} data-testid={`holiday-delete-${h.date}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
